/**
 * Where a piece's SETTINGS panel sits. It hangs below the header with its right
 * edge on the settings cog. On a phone held upright the cog is well left of the
 * screen's right edge (about 250px across on a 390px screen), so a panel as wide
 * as the screen started off the left edge and cut off its labels. It now keeps
 * a margin from both screen edges: it follows the cog where there is room and
 * slides right where there is not. On a 320px phone the header runs a few
 * pixels past the screen, cog included, so the right margin is kept too.
 */

/** The gap the panel keeps from the screen's edges. */
export const REP_SETTINGS_SCREEN_MARGIN_PX = 8;

// 420px of content plus 16px of padding each side, as it was; never wider than
// the screen less its margins.
export const REP_SETTINGS_PANEL_WIDTH = `min(452px, calc(100vw - ${2 * REP_SETTINGS_SCREEN_MARGIN_PX}px))`;

/**
 * The panel's width and horizontal place. `anchorRight` is the distance from
 * the screen's right edge that lines the panel up with the cog (`right` in CSS
 * pixels, as the header anchor gives it), or undefined before it is measured.
 */
export const repSettingsPanelPlacement = (anchorRight: string | undefined) => ({
  boxSizing: 'border-box' as const,
  width: REP_SETTINGS_PANEL_WIDTH,
  ...(anchorRight !== undefined && {
    right: `clamp(${REP_SETTINGS_SCREEN_MARGIN_PX}px, ${anchorRight}, calc(100vw - ${REP_SETTINGS_PANEL_WIDTH} - ${REP_SETTINGS_SCREEN_MARGIN_PX}px))`,
  }),
});

/**
 * How far the panel's caret sits in from the panel's right edge: zero where the
 * panel follows the cog, and as far as the panel slid right where it could not,
 * so the caret still points at the cog.
 */
export const repSettingsCaretRight = (anchorRight: string | undefined) =>
  anchorRight === undefined
    ? '0px'
    : `max(0px, calc(${anchorRight} - (100vw - ${REP_SETTINGS_PANEL_WIDTH} - ${REP_SETTINGS_SCREEN_MARGIN_PX}px)))`;

/** Below this screen width the panel's rows come in from 28px to 12px at each side. */
export const REP_SETTINGS_NARROW_SCREEN_PX = 400;

/**
 * The side margins of the panel's rows: 28px, and 12px on a screen under 400px
 * wide. On a 320px phone the panel is 304px wide; 28px a side left its fields
 * (230px at least) and the volume rows less room than they need, and they ran
 * past the panel's edge.
 */
export const repSettingsSideSx = {
  marginLeft: '28px',
  marginRight: '28px',
  [`@media (max-width: ${REP_SETTINGS_NARROW_SCREEN_PX - 0.02}px)`]: {
    marginLeft: '12px',
    marginRight: '12px',
  },
} as const;
